import React, { useState, useRef, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import EmojiGridBuilder from './EmojiGridBuilder';
import GenerationResults from './GenerationResults';
import { MODELS, DEFAULT_MODEL, buildIconPrompt } from './iconPrompts';
import '../../styles/icon-generator.css';
import UiIcon from '../ui/UiIcon';

const MAX_UPLOAD_DIMENSION = 2048;

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Could not read image'));
  img.src = src;
});

// Downscale and re-encode as PNG so any browser-decodable file stays under the callable size limit
const fileToPngDataUrl = async (file) => {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await loadImage(objectUrl);
    const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const IconGenerator = ({ isOpen, onClose, onIconsSaved }) => {
  const [mode, setMode] = useState('upload'); // 'upload' or 'gemini'
  const [subject, setSubject] = useState('');
  const [promptOverride, setPromptOverride] = useState(null);
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [generateSeparateImages, setGenerateSeparateImages] = useState(false);
  const [referenceImageBase64, setReferenceImageBase64] = useState(null);
  const [emojiCount, setEmojiCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const emojiGridRef = useRef(null);
  const fileInputRef = useRef(null);
  const functions = getFunctions();

  useEffect(() => {
    try {
      const savedSubject = localStorage.getItem('iconGenerator_subject');
      if (savedSubject) setSubject(savedSubject);
    } catch (e) { /* storage unavailable */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('iconGenerator_subject', subject);
    } catch (e) { /* storage unavailable */ }
  }, [subject]);

  const builtPrompt = buildIconPrompt({ subject });
  const effectivePrompt = promptOverride ?? builtPrompt;

  const handleCanvasChange = (base64Data) => {
    setReferenceImageBase64(base64Data);
    if (emojiGridRef.current) {
      setEmojiCount(emojiGridRef.current.getPlacements().length);
    }
  };

  const showImages = (urls) => {
    setImages(urls);
    setActiveImage(0);
  };

  const generateIndividualImages = (placements) => {
    return placements.map(({ row, col, emoji }) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const canvasSize = 1024;
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvasSize, canvasSize);

      ctx.font = `${canvasSize * 0.7}px "Noto Emoji", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'black';
      ctx.fillText(emoji, canvasSize / 2, canvasSize / 2);

      return { base64: canvas.toDataURL('image/png'), row, col, emoji };
    });
  };

  const handleGenerate = async () => {
    if (!effectivePrompt.trim() || (!promptOverride && !subject.trim())) {
      setError('Describe what the icon should show');
      return;
    }

    const placements = emojiGridRef.current ? emojiGridRef.current.getPlacements() : [];
    if (placements.length === 0) {
      setError('Add at least one reference emoji to match the style');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const generateIcon = httpsCallable(functions, 'generateIconWithGemini');

      if (generateSeparateImages && placements.length > 0) {
        const urls = [];
        for (const img of generateIndividualImages(placements)) {
          const prompt = promptOverride ?? buildIconPrompt({ subject });
          const result = await generateIcon({ referenceImageBase64: img.base64, prompt, model });
          urls.push(result.data.imageUrl);
        }
        showImages(urls);
      } else {
        const result = await generateIcon({
          referenceImageBase64,
          prompt: effectivePrompt.trim(),
          model
        });
        showImages([result.data.imageUrl]);
      }
    } catch (err) {
      console.error('Error generating icon:', err);
      setError(err.message || 'Failed to generate icon');
    } finally {
      setLoading(false);
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      showImages([await fileToPngDataUrl(file)]);
    } catch (err) {
      console.error('Error reading image:', err);
      setError(err.message || 'Failed to read image');
    } finally {
      setLoading(false);
    }
  };

  // Paste an image straight from the clipboard while the upload tab is open
  useEffect(() => {
    if (!isOpen || mode !== 'upload') return;
    const onPaste = (e) => {
      const item = Array.from(e.clipboardData?.items || []).find(i => i.type.startsWith('image/'));
      if (item) handleFile(item.getAsFile());
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [isOpen, mode]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  if (!isOpen) return null;

  return (
    <div className="icon-generator-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="icon-generator-modal">
        <div className="icon-generator-header">
          <h2>Add Icons</h2>
          <button onClick={onClose} className="close-btn" aria-label="Close"><UiIcon name="close" /></button>
        </div>

        <div className="icon-generator-content">
          <section className="generator-section">
            <div className="mode-tabs">
              <button className={mode === 'upload' ? 'active' : ''} onClick={() => setMode('upload')}>Upload image</button>
              <button className={mode === 'gemini' ? 'active' : ''} onClick={() => setMode('gemini')}>Generate with AI</button>
            </div>

            {mode === 'upload' && (
              <div
                className={`upload-dropzone${dragOver ? ' drag-over' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                <UiIcon name="upload" />
                <strong>{loading ? 'Reading image...' : 'Drop an image, paste it, or click to browse'}</strong>
                <span>Dark shapes on a light background trace best</span>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ''; }}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            {mode === 'gemini' && (
              <div className="gemini-controls">
                <label>
                  What should the icon show?
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => { setSubject(e.target.value); setPromptOverride(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleGenerate(); }}
                    placeholder="e.g. a seal wearing a party hat"
                    className="subject-input"
                  />
                </label>

                <div className="generator-block">
                  <h3>Reference emojis</h3>
                  <p className="section-description">
                    Black outline emoji the new icon should match in style.
                  </p>
                  <EmojiGridBuilder ref={emojiGridRef} onCanvasChange={handleCanvasChange} />
                  {emojiCount > 1 && model !== 'gemini-2.5-flash-image' && (
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={generateSeparateImages}
                        onChange={(e) => setGenerateSeparateImages(e.target.checked)}
                      />
                      <span>One separate image per emoji (uses one generation each)</span>
                    </label>
                  )}
                </div>

                <div className="generator-block">
                  <label>
                    Model
                    <select value={model} onChange={(e) => setModel(e.target.value)} className="model-select">
                      {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                  </label>
                  <label>
                    Prompt sent to Gemini
                    <textarea
                      value={effectivePrompt}
                      onChange={(e) => setPromptOverride(e.target.value)}
                      rows={4}
                      className="prompt-textarea"
                    />
                  </label>
                  {promptOverride !== null && (
                    <button className="btn-tertiary" onClick={() => setPromptOverride(null)}>Reset to template</button>
                  )}
                </div>

                <button
                  onClick={handleGenerate}
                  disabled={loading || (!promptOverride && !subject.trim())}
                  className="btn-primary"
                >
                  {loading ? 'Generating...' : 'Generate'}
                </button>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
          </section>

          {images.length > 1 && (
            <div className="image-picker">
              {images.map((url, i) => (
                <button key={url} className={i === activeImage ? 'active' : ''} onClick={() => setActiveImage(i)}>
                  <img src={url} alt={`Result ${i + 1}`} />
                </button>
              ))}
            </div>
          )}

          {images[activeImage] && (
            <GenerationResults
              key={images[activeImage]}
              imageUrl={images[activeImage]}
              onIconsSaved={onIconsSaved}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IconGenerator;
